import { useTranslation } from 'react-i18next';
import Chart from 'react-apexcharts';
import type { ApexOptions } from 'apexcharts';
import { Card, CardTitle } from '@/components/ui';
import { useTheme } from '@/hooks/useTheme';

interface ClicksChartProps {
  data: { date: string; clicks: number }[];
  title?: string;
}

const ClicksChart = ({ data, title }: ClicksChartProps) => {
  const { t, i18n } = useTranslation();
  const { isDark } = useTheme();
  const displayTitle = title ?? t('stats.clicksOverTime');

  if (data.length === 0) {
    return (
      <Card variant="default" padding="lg">
        <CardTitle className="mb-6">{displayTitle}</CardTitle>
        <div className="h-[300px] flex items-center justify-center text-gray-400">
          {t('common.noData')}
        </div>
      </Card>
    );
  }

  const categories = data.map((item) =>
    new Date(item.date).toLocaleDateString(i18n.language, {
      month: 'short',
      day: 'numeric',
    })
  );

  const series = [
    {
      name: t('stats.clicks'),
      data: data.map((item) => item.clicks),
    },
  ];

  const options: ApexOptions = {
    chart: {
      type: 'area',
      toolbar: { show: false },
      zoom: { enabled: false },
      background: 'transparent',
      fontFamily: 'Inter, system-ui, sans-serif',
    },
    theme: {
      mode: isDark ? 'dark' : 'light',
    },
    stroke: {
      curve: 'smooth',
      width: 3,
      colors: ['#D4E157'],
    },
    fill: {
      type: 'gradient',
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.3,
        opacityTo: 0,
        stops: [0, 100],
        colorStops: [
          {
            offset: 0,
            color: '#D4E157',
            opacity: 0.3,
          },
          {
            offset: 100,
            color: '#D4E157',
            opacity: 0,
          },
        ],
      },
    },
    colors: ['#D4E157'],
    xaxis: {
      categories,
      labels: {
        style: {
          colors: isDark ? '#9CA3AF' : '#6B7280',
          fontSize: '12px',
        },
      },
      axisBorder: {
        color: isDark ? '#374151' : '#E5E7EB',
      },
      axisTicks: { show: false },
    },
    yaxis: {
      labels: {
        style: {
          colors: isDark ? '#9CA3AF' : '#6B7280',
          fontSize: '12px',
        },
      },
    },
    grid: {
      borderColor: isDark ? '#374151' : '#f0f0f0',
      strokeDashArray: 3,
    },
    tooltip: {
      theme: isDark ? 'dark' : 'light',
      style: {
        fontSize: '14px',
      },
      y: {
        formatter: (value: number) => `${value} ${t('stats.clicks').toLowerCase()}`,
      },
    },
    dataLabels: {
      enabled: false,
    },
  };

  return (
    <Card variant="default" padding="lg">
      <CardTitle className="mb-6">{displayTitle}</CardTitle>
      <div className="h-[300px]">
        <Chart
          options={options}
          series={series}
          type="area"
          height="100%"
          width="100%"
        />
      </div>
    </Card>
  );
};

export default ClicksChart;
